import React from "react";
import {useAppDispatch, useAppSelector} from "../../../../app/store";
import {changeGameModal} from "../../../../app/slices/modals/gameEditModal";
import {ADMIN_LANG, FEATURE_NOTIFICATIONS} from "../../../../settings";
import {Accordion, Col, Form, Row} from "react-bootstrap";
import GameSettings from "./GameSettings";
import ThemeSettings from "./ThemeSettings";
import Screens from "./Screens";

type Props = {
    language: string
    newGame: boolean
}

const CommonFormFields = ({language, newGame}: Props) => {
    const dispatch = useAppDispatch();
    const {
        languages,
        languageTab,
        version_id,
        notification_emails
    } = useAppSelector((state) => state.gameEditModal);


    const handleNameChange = (name: string) => {
        let languageExists = false;

        const updatedLanguages = languages.map((language) => {
            if (language.language_code === languageTab) {
                languageExists = true;
                return {...language, name: name};
            }
            return language;
        });

        if (!languageExists) {
            updatedLanguages.push({language_code: languageTab, name: name});
        }
        dispatch(changeGameModal({languages: updatedLanguages}));
    };

    const handleSlugChange = (slug: string) => {
        const updatedLanguages = languages.map((language) => {
            if (language.language_code === languageTab) {
                return {...language, slug: slug};
            }
            return language;
        });
        dispatch(changeGameModal({languages: updatedLanguages}));
    }

    const handleIsActiveChange = (isActive: boolean) => {
        const updatedLanguages = languages.map((language) => {
            if (language.language_code === languageTab) {
                return {...language, is_active: isActive};
            }
            return language;
        });
        dispatch(changeGameModal({languages: updatedLanguages}));
    }

    const helpTextName = languages
        .filter(lang => lang.language_code !== language && lang.name)
        .map(lang => (
            <div key={lang.language_code}>
                <strong>{lang.language_code.toUpperCase()}: </strong>
                {lang.name}
            </div>
        ))

    return (
        <Row className='gy-3'>
            <Col xs={12}>
                <Form.Group controlId={`title_${language}`}>
                    <Form.Label>{`${ADMIN_LANG.name} (${language.toUpperCase()})`}</Form.Label>
                    <Form.Control
                        type='text'
                        maxLength={255}
                        onChange={(e) => handleNameChange(e.target.value)}
                        value={languages.find((lang) => lang.language_code === language)?.name || ''}
                        required={languageTab === language}
                    />
                    {helpTextName.length > 0 &&
                        <Form.Text className='help-text'>
                            {helpTextName}
                        </Form.Text>
                    }
                </Form.Group>
            </Col>
            {!newGame &&
                <>
                    <Col xs={12}>
                        <Form.Group className='pb-3' controlId={`slug_${languageTab}`}>
                            <Form.Label>{`${ADMIN_LANG.slug} (${languageTab.toUpperCase()})`}</Form.Label>
                            <Form.Control
                                maxLength={255}
                                onChange={(e) => handleSlugChange(e.target.value)}
                                value={languages.find((lang) => lang.language_code === languageTab)?.slug || ''}
                                type='text'
                            />
                        </Form.Group>
                        <Form.Group controlId={`is_active_${languageTab}`}>
                            <Form.Switch
                                checked={languages.find((lang) => lang.language_code === languageTab)?.is_active || false}
                                onChange={(e) => {
                                    handleIsActiveChange(e.target.checked)
                                }}
                                className='form-label'
                                disabled={!version_id}
                                label={`${ADMIN_LANG.is_active} (${languageTab.toUpperCase()})`}
                            />
                            <Form.Text className='help-text'>
                                {ADMIN_LANG.is_active_help_text}
                            </Form.Text>
                        </Form.Group>
                    </Col>

                    <Accordion>
                        <Accordion.Item eventKey="0">
                            <Accordion.Header>{ADMIN_LANG.game_settings}</Accordion.Header>
                            <GameSettings languageTab={language}/>
                        </Accordion.Item>
                        <Accordion.Item eventKey="1">
                            <Accordion.Header>{ADMIN_LANG.theme_and_lift_settings}</Accordion.Header>
                            <ThemeSettings languageTab={language}/>
                        </Accordion.Item>
                        <Accordion.Item eventKey="2">
                            <Accordion.Header>{ADMIN_LANG.screens}</Accordion.Header>
                            <Screens languageTab={language}/>
                        </Accordion.Item>
                        {FEATURE_NOTIFICATIONS &&
                            <Accordion.Item eventKey="3">
                                <Accordion.Header>{ADMIN_LANG.notifications}</Accordion.Header>
                                <Accordion.Body>
                                    <Row className='gy-3'>
                                        <Col xs={12}>
                                            <Form.Group controlId={`notification_emails_${languageTab}`}>
                                                <Form.Label>Vastaanottajat</Form.Label>
                                                <textarea className='form-control' value={notification_emails} onChange={(e) => dispatch(changeGameModal({notification_emails: e.target.value}))}/>
                                                <Form.Text className='help-text'>
                                                    Kirjoita sähköpostiosoitteet pilkulla eroteltuna. <br></br>
                                                    Ilmoitukset läpäistyistä suorituksista lähetetään annetuille sähköpostiosoitteille.
                                                </Form.Text>
                                            </Form.Group>
                                        </Col>
                                    </Row>
                                </Accordion.Body>
                            </Accordion.Item>
                        }
                    </Accordion>
                </>
            }
        </Row>
);
}

export default CommonFormFields;

